import { create } from 'zustand';
import { MediaProgram } from './mediaPrograms';
import { getApiUrl } from "@/utils/apiConfig";

interface EmissionsState {
  emissions: MediaProgram[];
  isLoading: boolean;
  error: string | null;
  fetchEmissions: (publicOnly?: boolean) => Promise<void>;
  saveEmission: (emission: Partial<MediaProgram>) => Promise<MediaProgram | null>;
  updateEmissionStatus: (id: string, status: 'approved' | 'rejected', reason?: string) => Promise<boolean>;
  deleteEmission: (id: string) => Promise<boolean>;
}

const API_URL = `${getApiUrl()}/emissions.php`;

const getAuthToken = () => localStorage.getItem('kaynin_auth_token');

export const useEmissionsStore = create<EmissionsState>((set, get) => ({
  emissions: [],
  isLoading: false,
  error: null,

  fetchEmissions: async (publicOnly = false) => {
    set({ isLoading: true, error: null });
    try {
      const token = getAuthToken();
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const url = publicOnly ? `${API_URL}?public=1` : API_URL;
      
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error('Erreur lors de la récupération des émissions');
      
      const data = await response.json();
      set({ emissions: data, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      console.error("Fetch emissions error:", error);
    }
  },

  saveEmission: async (emission) => {
    set({ isLoading: true, error: null });
    try {
      const token = getAuthToken();
      const isUpdate = !!emission.id;
      const method = isUpdate ? 'PUT' : 'POST';
      
      const response = await fetch(API_URL, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(emission)
      });

      if (!response.ok) throw new Error("Erreur lors de l'enregistrement de l'émission");
      
      const data = await response.json();
      
      // Update local state instead of doing a full fetch if possible
      if (isUpdate) {
        set(state => ({
          emissions: state.emissions.map(e => e.id === emission.id ? { ...e, ...emission } as MediaProgram : e),
          isLoading: false
        }));
        return { ...emission } as MediaProgram;
      } else {
        const newEmission = { ...emission, id: data.id } as MediaProgram;
        set(state => ({
          emissions: [newEmission, ...state.emissions],
          isLoading: false
        }));
        return newEmission;
      }
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      console.error("Save emission error:", error);
      return null;
    }
  },

  updateEmissionStatus: async (id, status, reason) => {
    set({ isLoading: true, error: null });
    try {
      const token = getAuthToken();
      const response = await fetch(API_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ id, status, rejectionReason: reason })
      });

      if (!response.ok) throw new Error('Erreur lors de la mise à jour du statut');
      
      set(state => ({
        emissions: state.emissions.map(e => 
          e.id === id ? { ...e, status, rejectionReason: reason } : e
        ),
        isLoading: false
      }));
      return true;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      console.error("Update emission status error:", error);
      return false;
    }
  },

  deleteEmission: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_URL}?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Erreur lors de la suppression');
      
      set(state => ({
        emissions: state.emissions.filter(e => e.id !== id),
        isLoading: false
      }));
      return true;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      console.error("Delete emission error:", error);
      return false;
    }
  }
}));
